import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FileText, Loader } from 'lucide-react'
import PaymentConfirmationDialog from './PaymentConfirmationDialog'
import {
  API_SPONSORSHIP_STATUS,
  type AdPlacementPlanType,
  type SponsorShipPlan,
  type SponsorshipDetailsType,
} from '@/schemas/sponsor.schems'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  sponsorShipCheckout,
  type SponsorShipPlayload,
  getSponsorEventDetails,
} from '@/api/sponsorApi'
import { queryClient } from '@/main'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { handleAxiosError } from '@/utils/helper'
import { useNavigate } from 'react-router-dom'
import { PATHROUTES } from '@/constants/path'

interface SponsorshipSummaryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTier?: SponsorShipPlan
  selectedPlacements?: AdPlacementPlanType[]
  eventId?: string
  sponsorShipId?: string
}

function SponsorshipSummaryDialog({
  open,
  onOpenChange,
  selectedTier,
  eventId,
  sponsorShipId,
}: Readonly<SponsorshipSummaryDialogProps>) {
  const [isPaymentConfirmationOpen, setIsPaymentConfirmationOpen] =
    useState(false)
  const navigate = useNavigate()

  const { mutate: checkoutMutation, isPending: isCheckoutPending } =
    useMutation({
      mutationFn: sponsorShipCheckout,
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: [SPONSOR_QUERY_KEYS.sponsorShipPlansById(eventId!)],
        })
        if (data && data?.url) {
          onOpenChange(false)
          window.location.href = data?.url
        } else if (data && data?.id) {
          if (data?.status === API_SPONSORSHIP_STATUS.ACTIVE) {
            navigate(
              `${PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(data.id)}?source=checkout`
            )
          } else {
            navigate(PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT)
          }
        }
      },
      onError: error => {
        handleAxiosError(
          'An error occurred during checkout. Please try again.',
          error
        )
      },
    })

  const isFetchDetails = !selectedTier && !!sponsorShipId && open
  const {
    data: sponsorShipDetailsData,
    isLoading: isLoadingSponsorShipDetails,
  } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.byId(sponsorShipId!),
    queryFn: () => getSponsorEventDetails(sponsorShipId!),
    enabled: isFetchDetails,
  })

  const sponsorShipDetails: SponsorshipDetailsType | undefined =
    sponsorShipDetailsData?.data

  const effectiveSelectedTier =
    selectedTier ||
    (sponsorShipDetails?.plan
      ? {
          id: sponsorShipDetails.plan.id,
          name: sponsorShipDetails.plan.name,
          price: sponsorShipDetails.amount || '0',
        }
      : undefined)

  // Calculate total amount
  const tierPrice = effectiveSelectedTier?.price || 0
  const totalAmount = Number(tierPrice)

  const handleCancel = () => {
    onOpenChange(false)
  }

  const handleProceedToPayment = () => {
    if (effectiveSelectedTier) {
      const selectedTierId = effectiveSelectedTier?.id

      const payload: SponsorShipPlayload = {
        plan: selectedTierId,
      }

      checkoutMutation({
        id: sponsorShipId!,
        payload,
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-3xl w-[90%] p-3.5 sm:p-6"
          showCloseButton={false}
        >
          <div className="flex flex-col gap-4">
            {/* Featured Icon */}
            <div className="w-10 h-10 bg-ss-blue-50 rounded-full flex items-center justify-center shrink-0 self-center sm:self-start">
              <FileText
                className="w-5 h-5 text-ss-blue-500"
                aria-hidden="true"
              />
            </div>

            {/* Dialog Header */}
            <DialogHeader className="text-center sm:text-left">
              <DialogTitle className="text-lg font-semibold text-ss-neutral-950 leading-7">
                Sponsorship Summary & Checkout
              </DialogTitle>
            </DialogHeader>

            {isLoadingSponsorShipDetails ? (
              <div className="flex justify-center items-center py-8">
                <Loader className="animate-spin text-ss-neutral-600" />
              </div>
            ) : (
              <>
                {/* Content Section */}
                <div className="flex flex-col gap-6">
                  <div className="bg-ss-neutral-25 border border-ss-neutral-50 rounded-2xl p-5 space-y-4">
                    {/* Selected Tier Section */}
                    {effectiveSelectedTier && (
                      <section className="space-y-3">
                        <h3 className="text-base font-bold text-ss-neutral-950 leading-[22px]">
                          Selected Tier
                        </h3>
                        <div className="flex items-center justify-between text-base text-ss-neutral-950 leading-[22px]">
                          <span className="font-normal">
                            {effectiveSelectedTier.name}
                          </span>
                          <span className="font-semibold whitespace-nowrap">
                            ${effectiveSelectedTier.price.toLocaleString()}
                          </span>
                        </div>
                      </section>
                    )}

                    {/* Divider */}
                    <div className="h-px bg-ss-neutral-50" />

                    {/* Total Amount Section */}
                    <div className="flex items-center justify-between text-base text-ss-neutral-950 leading-[22px]">
                      <span className="font-bold">Total Amount</span>
                      <span className="font-semibold whitespace-nowrap">
                        ${totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 text-sm font-semibold hover:bg-ss-neutral-25 rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProceedToPayment}
                    className="h-9 px-4 bg-ss-blue-900 text-white text-sm font-semibold hover:bg-ss-blue-800 rounded-lg"
                    disabled={isCheckoutPending}
                  >
                    Proceed to Payment
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <PaymentConfirmationDialog
        open={isPaymentConfirmationOpen}
        onOpenChange={setIsPaymentConfirmationOpen}
        eventName={effectiveSelectedTier?.name || 'Event'}
        sponsorShipId={sponsorShipId}
      />
    </>
  )
}

export default SponsorshipSummaryDialog
